import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import axios from "axios";
import {
  Users, Download, RefreshCw, ChevronDown, ChevronUp,
  Phone, Mail, MapPin, Clock, Filter, ArrowLeft, Calculator,
  CheckCircle2, AlertCircle, Loader2, Settings, ExternalLink, FileSpreadsheet
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  converted: "bg-green-100 text-green-700",
  closed: "bg-slate-100 text-slate-600",
};

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [expandedLead, setExpandedLead] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [showIntegrations, setShowIntegrations] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, leadsRes, integrationsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/leads`, { params: { status: filter || undefined, limit: 200 } }),
        axios.get(`${API}/integrations/status`),
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
      setIntegrations(integrationsRes.data);
    } catch (err) {
      console.error("Failed to fetch admin data", err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (leadId, newStatus) => {
    setUpdating(leadId);
    try {
      await axios.patch(`${API}/leads/${leadId}`, { status: newStatus });
      await fetchData();
    } catch (err) {
      console.error("Failed to update lead", err);
    } finally {
      setUpdating(null);
    }
  };

  const exportCSV = () => {
    window.open(`${API}/leads/export/csv`, "_blank");
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="text-slate-500 hover:text-brand-600 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </a>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Fix My AC — Lead Dashboard</h1>
              <p className="text-xs text-slate-500">Manage inquiries and service leads</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => setShowIntegrations(!showIntegrations)} variant="outline" size="sm" className="text-xs">
              <Settings className="w-3.5 h-3.5 mr-1.5" />
              Integrations
            </Button>
            <Button onClick={fetchData} variant="outline" size="sm" className="text-xs">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button onClick={exportCSV} size="sm" className="bg-brand-600 hover:bg-brand-700 text-white text-xs">
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Export CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Integrations Panel */}
        {showIntegrations && integrations && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Settings className="w-5 h-5 text-brand-600" />
                Integration Settings
              </h2>
              <button onClick={() => setShowIntegrations(false)} className="text-slate-400 hover:text-slate-600">
                <ChevronUp className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-6">
              {/* Email Integration */}
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-brand-500" />
                    <h3 className="font-medium text-slate-800">Email Notifications</h3>
                  </div>
                  {integrations.email?.enabled ? (
                    <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 text-xs">Not Configured</Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  {integrations.email?.enabled 
                    ? `Notifications sent to: ${integrations.email.admin_email}`
                    : "Configure Resend API key to enable email alerts for new leads."}
                </p>
                {integrations.email?.enabled ? (
                  <div className="flex items-center gap-2 text-xs text-green-600">
                    <CheckCircle2 className="w-4 h-4" />
                    Admin alerts & customer confirmations enabled
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-md p-3 text-xs text-slate-600">
                    <p className="font-medium mb-2">Setup Steps:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Sign up at <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">resend.com</a></li>
                      <li>Get your API key from the dashboard</li>
                      <li>Add RESEND_API_KEY to backend .env</li>
                    </ol>
                  </div>
                )}
              </div>

              {/* Google Sheets Integration */}
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5 text-green-500" />
                    <h3 className="font-medium text-slate-800">Google Sheets</h3>
                  </div>
                  {integrations.google_sheets?.is_ready ? (
                    <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 text-xs">Not Configured</Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  {integrations.google_sheets?.is_ready 
                    ? "Leads are automatically synced to Google Sheets."
                    : "Connect to automatically log all leads to a Google Sheet."}
                </p>
                {integrations.google_sheets?.is_ready ? (
                  <a 
                    href={integrations.google_sheets.sheet_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-brand-600 hover:text-brand-700 font-medium"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open Google Sheet
                  </a>
                ) : (
                  <div className="bg-slate-50 rounded-md p-3 text-xs text-slate-600">
                    <p className="font-medium mb-2">Setup Steps:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      {integrations.google_sheets?.missing_steps?.map((step, i) => (
                        <li key={i}>{step}</li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Total Leads</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total_leads}</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">New Leads</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">{stats.new_leads}</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Today</p>
              <p className="text-3xl font-bold text-accent-500 mt-1">{stats.today_leads}</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">This Week</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{stats.this_week_leads}</p>
            </div>
          </div>
        )}

        {/* Breakdown */}
        {stats && (
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-3">By Status</p>
              <div className="space-y-2">
                {Object.entries(stats.by_status || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <Badge className={`text-xs ${statusColors[k] || "bg-slate-100 text-slate-600"}`}>{k}</Badge>
                    <span className="text-sm font-semibold text-slate-800">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-3">By Calculator</p>
              <div className="space-y-2">
                {Object.entries(stats.by_calculator || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-xs text-slate-600">{k || "Direct Form"}</span>
                    <span className="text-sm font-semibold text-slate-800">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-3">By Source</p>
              <div className="space-y-2">
                {Object.entries(stats.by_source || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-xs text-slate-600">{k}</span>
                    <span className="text-sm font-semibold text-slate-800">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-3 mb-5">
          <Filter className="w-4 h-4 text-slate-400" />
          <div className="flex gap-2">
            {["", "new", "contacted", "converted", "closed"].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  filter === f ? "bg-brand-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                }`}>
                {f || "All"}
              </button>
            ))}
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {leads.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No leads found.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {leads.map((lead) => (
                <div key={lead.id} className="hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between px-5 py-4 cursor-pointer"
                    onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}>
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-brand-700">
                          {lead.name?.split(" ").map(n => n[0]).join("").slice(0, 2)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{lead.name}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-2">
                          <Phone className="w-3 h-3" /> {lead.phone}
                          {lead.city && <><MapPin className="w-3 h-3 ml-1" /> {lead.city}</>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {lead.calculator_type && (
                        <Badge variant="outline" className="text-[10px] border-brand-200 text-brand-600">
                          <Calculator className="w-3 h-3 mr-1" />
                          {lead.calculator_type}
                        </Badge>
                      )}
                      {lead.calculated_tonnage && (
                        <span className="text-xs font-medium text-accent-600">{lead.calculated_tonnage}T</span>
                      )}
                      <Badge className={`text-[10px] ${statusColors[lead.status] || statusColors.new}`}>
                        {lead.status}
                      </Badge>
                      <span className="text-[10px] text-slate-400 hidden sm:block">
                        {new Date(lead.timestamp).toLocaleDateString()}
                      </span>
                      {expandedLead === lead.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>

                  {/* Expanded */}
                  {expandedLead === lead.id && (
                    <div className="px-5 pb-5 pt-1 bg-slate-50 border-t border-slate-100">
                      <div className="grid sm:grid-cols-2 gap-4 mb-4">
                        <div className="space-y-2">
                          {lead.email && <p className="text-xs text-slate-600 flex items-center gap-1.5"><Mail className="w-3 h-3" /> {lead.email}</p>}
                          {lead.service_required && <p className="text-xs text-slate-600">Service: <strong>{lead.service_required}</strong></p>}
                          {lead.suggested_system && <p className="text-xs text-slate-600">System: <strong>{lead.suggested_system}</strong></p>}
                          <p className="text-xs text-slate-400 flex items-center gap-1.5"><Clock className="w-3 h-3" /> {new Date(lead.timestamp).toLocaleString()}</p>
                        </div>
                        {lead.calculator_inputs && (
                          <div className="bg-white rounded-lg p-3 border border-slate-200">
                            <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Calculator Data</p>
                            <div className="space-y-1">
                              {Object.entries(lead.calculator_inputs).map(([k, v]) => (
                                <div key={k} className="flex justify-between text-xs">
                                  <span className="text-slate-500">{k.replace(/([A-Z])/g, ' $1').trim()}</span>
                                  <span className="font-medium text-slate-700">{String(v)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      {lead.message && (
                        <div className="bg-white rounded-lg p-3 border border-slate-200 mb-4">
                          <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Message</p>
                          <p className="text-xs text-slate-700">{lead.message}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 mr-2">Update status:</span>
                        {["new", "contacted", "converted", "closed"].map(s => (
                          <button key={s} onClick={() => updateStatus(lead.id, s)} disabled={updating === lead.id || lead.status === s}
                            className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                              lead.status === s
                                ? "bg-brand-600 text-white"
                                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                            }`}>
                            {updating === lead.id ? "..." : s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
